import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { IResourceDatabaseView } from '@/api/types';
import type { RootState } from '@/store';
import type { ISelectedFolder } from '@/store/types';

import { LoaderWrapper, ResourcesBreadcrumb, ResourcesTabsTableContainer } from '@/components/common';
import { getSelectedFolderName } from '@/helpers';
import { ResourceDatabaseTitleAction } from '../ResourceDatabaseTitleAction';
import { ResourceDatabaseViewsContainer } from '../ResourceDatabaseViewsContainer';
import { ResourceDatabaseFieldsContainer } from '../ResourceDatabaseFieldsContainer';
import { selectFoldersList, selectPendingAction } from '@/store/selectors/resourcesSelectors';
import { combineDatabaseFieldsToShow, combineItemsToShow } from '@/store/combiners/resourcesCombiners';
import { useAppDispatch, useAppSelector, useResourceNavigation } from '@/hooks';
import { getDatabase } from '@/store/slices/resourcesSlice';
import { ResourceDatabaseRouteSkeleton } from './ResourceDatabaseRoute.skeleton';

const resourceDatabaseRouteSelector = createSelector(
  selectFoldersList,
  selectPendingAction(getDatabase.typePrefix),
  (_: RootState, selectedFolder: ISelectedFolder) => selectedFolder,
  (foldersList, isDatabasePending, selectedFolder) => ({
    foldersList,
    isDatabasePending,
    itemsToShow: combineItemsToShow(foldersList, selectedFolder),
    databaseFieldsToShow: combineDatabaseFieldsToShow(foldersList, selectedFolder),
  }),
);

const ResourceDatabaseRoute = (): React.ReactElement => {
  const { t } = useTranslation();
  const { selectedFolder } = useResourceNavigation();
  const { foldersList, itemsToShow, databaseFieldsToShow, isDatabasePending } = useAppSelector(state =>
    resourceDatabaseRouteSelector(state, selectedFolder),
  );
  const dispatch = useAppDispatch();
  const displayedDbIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (selectedFolder.folderId === displayedDbIdRef.current) {
      return;
    }

    const getDatabaseRequest = dispatch(
      getDatabase({
        clientId: selectedFolder.clientId,
        dbId: selectedFolder.folderId,
      }),
    );

    displayedDbIdRef.current = selectedFolder.folderId;

    return () => getDatabaseRequest.abort();
  }, [dispatch, selectedFolder.clientId, selectedFolder.folderId]);

  return (
    <>
      <ResourcesBreadcrumb />
      <FullHeightLoaderWrapper
        isLoading={selectedFolder.folderId !== displayedDbIdRef.current || isDatabasePending}
        loader={<ResourceDatabaseRouteSkeleton />}
      >
        <ResourcesTabsTableContainer
          tableTitle={t(getSelectedFolderName(foldersList || [], selectedFolder))}
          tableTitleActionSlot={
            <ResourceDatabaseTitleAction
              label={t`resources.content.manageDatabase`}
              dropdownConfig={{
                options: [
                  {
                    label: t`resources.databases.options.generalSettings`,
                    callback: () => '',
                  },
                ],
              }}
            />
          }
          defaultKey="views"
          tabs={[
            {
              key: 'views',
              title: t('resources.content.viewsWithCount', { count: (itemsToShow as IResourceDatabaseView[]).length }),
              content: (
                <TabsResourceDatabaseViewsContainer
                  items={itemsToShow as IResourceDatabaseView[]}
                  clientId={selectedFolder.clientId}
                  databaseId={selectedFolder.folderId}
                />
              ),
            },
            {
              key: 'fields',
              title: t('resources.content.fieldsWithCount', { count: databaseFieldsToShow?.length }),
              content: <TabsResourceDatabaseFieldsContainer items={databaseFieldsToShow} />,
            },
          ]}
        />
      </FullHeightLoaderWrapper>
    </>
  );
};

export default ResourceDatabaseRoute;

const FullHeightLoaderWrapper = styled(LoaderWrapper)`
  height: 100%;
`;

const TabsResourceDatabaseViewsContainer = styled(ResourceDatabaseViewsContainer)`
  margin-top: 0;
  border-top-left-radius: 0;
`;

const TabsResourceDatabaseFieldsContainer = styled(ResourceDatabaseFieldsContainer)`
  margin-top: 0;
  border-top-left-radius: 0;
`;
